import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { MagnifyingGlassIcon, AcademicCapIcon, DocumentTextIcon, VideoCameraIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { SingleColumnTemplate } from '../../../../ui/layouts/SingleColumnTemplate';
import Card from '../../../../ui/canon/Card';
import Avatar from '../StudentPlatform/shared/Avatar';
import StudentProfileModal from './StudentProfileModal';

const StudentCard = ({ student, onClick, isMessaging }) => {
  const displayName = student.user?.allowed_name || student.user?.name || 'Student';
  const avatarUrl = student.avatar_url; // Student's custom uploaded avatar
  const isEmployeeCandidate = student.candidate_track === 'employee';
  const candidateTypeLabel = isEmployeeCandidate ? 'Employee Candidate' : 'Student Candidate';

  return (
    <div
      onClick={() => onClick(student)}
      className="bg-succession-bg-primary border border-succession-border rounded-lg p-6 hover:shadow-succession-lg transition-all duration-200 cursor-pointer hover:border-succession-primary relative"
    >
      {/* Avatar */}
      <div className="flex items-center gap-4 mb-4">
        <div className="relative flex-shrink-0">
          <Avatar 
            src={avatarUrl} 
            name={displayName} 
            size="xl"
            className="border-2 border-succession-border"
          />
          {/* Messaging Indicator - Top Right of Avatar */}
          {isMessaging && (
            <div className="absolute -top-1 -right-1 flex items-center justify-center w-7 h-7 bg-blue-50 border-2 border-blue-200 rounded-full">
              <ChatBubbleLeftRightIcon className="h-4 w-4 text-blue-600" />
            </div>
          )}
        </div>
        <span className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-semibold mb-4 ${
          isEmployeeCandidate ? 'bg-indigo-100 text-indigo-700' : 'bg-sky-100 text-sky-700'
        }`}>
          {candidateTypeLabel}
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-succession-text-primary truncate">
            {displayName}
          </h3>
          {student.school && (
            <p className="text-sm text-succession-text-secondary truncate">
              {student.school}
            </p>
          )}
        </div>
      </div>

      {/* Info Grid */}
      <div className="space-y-2 mb-4">
        {student.degree_program && !isEmployeeCandidate && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-succession-text-secondary">Program:</span>
            <span className="text-succession-text-primary font-medium">{student.degree_program}</span>
          </div>
        )}
        {student.graduation_year && !isEmployeeCandidate && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-succession-text-secondary">Graduating:</span>
            <span className="text-succession-text-primary font-medium">{student.graduation_year}</span>
          </div>
        )}
        {(student.city || student.state) && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-succession-text-secondary">Location:</span>
            <span className="text-succession-text-primary font-medium">
              {[student.city, student.state].filter(Boolean).join(', ')}
            </span>
          </div>
        )}
        {(student.employment_type || student.experience_level) && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-succession-text-secondary">{isEmployeeCandidate ? 'Experience:' : 'Seeking:'}</span>
            <span className="text-succession-text-primary font-medium">
              {isEmployeeCandidate ? (student.experience_level || 'Not specified') : (student.employment_type === 'internship' ? 'Internship' : 'Full-Time')}
            </span>
          </div>
        )}
      </div>

      {/* Certifications */}
      {student.certifications && student.certifications.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-4">
          {student.certifications.slice(0, 3).map((cert, idx) => (
            <span key={idx} className="text-xs px-2 py-1 rounded border border-succession-border text-succession-text-secondary bg-succession-bg-secondary">
              {cert}
            </span>
          ))}
          {student.certifications.length > 3 && (
            <span className="text-xs px-2 py-1 rounded text-succession-text-secondary">
              +{student.certifications.length - 3}
            </span>
          )}
        </div>
      )}

      {/* Resume & Video Indicators */}
      <div className="flex items-center gap-3 pt-3 border-t border-succession-border">
        <div className={`flex items-center gap-1.5 text-xs ${student.resume_url ? 'text-succession-primary' : 'text-succession-text-muted'}`}>
          <DocumentTextIcon className="h-4 w-4" />
          <span>{student.resume_url ? 'Resume' : 'No Resume'}</span>
        </div>
        <div className={`flex items-center gap-1.5 text-xs ${student.video_url ? 'text-succession-primary' : 'text-succession-text-muted'}`}>
          <VideoCameraIcon className="h-4 w-4" />
          <span>{student.video_url ? 'Video' : 'No Video'}</span>
        </div>
      </div>
    </div>
  );
};

StudentCard.propTypes = {
  student: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
  isMessaging: PropTypes.bool,
};

const StudentProspectsPage = ({ currentUser, students, onFilterChange, filters, messagingWithStudentIds }) => {
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [localFilters, setLocalFilters] = useState(filters || {});

  const handleFilterChange = (key, value) => {
    const newFilters = { ...localFilters, [key]: value || null };
    setLocalFilters(newFilters);
    onFilterChange(newFilters);
  };

  const clearFilters = () => {
    const emptyFilters = {
      school: null,
      degree_program: null,
      graduation_year: null,
      employment_type: null,
      candidate_track: null,
      state: null,
    };
    setLocalFilters(emptyFilters);
    setSearchTerm('');
    onFilterChange(emptyFilters);
  };

  const filteredStudents = students.filter(student => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const displayName = student.user?.allowed_name || student.user?.name || '';
    const school = student.school || '';
    const degree = student.degree_program || '';
    return displayName.toLowerCase().includes(searchLower) ||
           school.toLowerCase().includes(searchLower) ||
           degree.toLowerCase().includes(searchLower);
  });

  const hasActiveFilters = Object.values(localFilters).some(v => v) || searchTerm;

  return (
    <SingleColumnTemplate
      title="Student Prospects"
      subtitle="Browse and connect with talented students seeking opportunities in financial services"
      variant="wide"
    >
      {/* Filters */}
      <Card className="p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-succession-text-primary">Filters</h2>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-sm text-succession-primary hover:text-succession-primary-dark font-medium"
              >
                Clear All
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {/* Search */}
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-succession-text-muted" />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 rounded-md border border-succession-border bg-white dark:bg-gray-800 text-succession-text-primary focus:outline-none focus:ring-2 focus:ring-succession-primary"
              />
            </div>

            {/* School */}
            <input
              type="text"
              placeholder="School"
              value={localFilters.school || ''}
              onChange={(e) => handleFilterChange('school', e.target.value)}
              className="px-3 py-2 rounded-md border border-succession-border bg-white dark:bg-gray-800 text-succession-text-primary focus:outline-none focus:ring-2 focus:ring-succession-primary"
            />

            {/* Degree Program */}
            <input
              type="text"
              placeholder="Degree Program"
              value={localFilters.degree_program || ''}
              onChange={(e) => handleFilterChange('degree_program', e.target.value)}
              className="px-3 py-2 rounded-md border border-succession-border bg-white dark:bg-gray-800 text-succession-text-primary focus:outline-none focus:ring-2 focus:ring-succession-primary"
            />

            {/* Employment Type */}
            <select
              value={localFilters.employment_type || ''}
              onChange={(e) => handleFilterChange('employment_type', e.target.value)}
              className="px-3 py-2 rounded-md border border-succession-border bg-white dark:bg-gray-800 text-succession-text-primary focus:outline-none focus:ring-2 focus:ring-succession-primary"
            >
              <option value="">All Types</option>
              <option value="internship">Internships</option>
              <option value="full-time">Full-Time</option>
            </select>

            <select
              value={localFilters.candidate_track || ''}
              onChange={(e) => handleFilterChange('candidate_track', e.target.value)}
              className="px-3 py-2 rounded-md border border-succession-border bg-white dark:bg-gray-800 text-succession-text-primary focus:outline-none focus:ring-2 focus:ring-succession-primary"
            >
              <option value="">All Candidate Types</option>
              <option value="student">Student</option>
              <option value="employee">Employee</option>
            </select>

            {/* State */}
            <input
              type="text"
              placeholder="State"
              value={localFilters.state || ''}
              onChange={(e) => handleFilterChange('state', e.target.value)}
              className="px-3 py-2 rounded-md border border-succession-border bg-white dark:bg-gray-800 text-succession-text-primary focus:outline-none focus:ring-2 focus:ring-succession-primary"
            />
          </div>
        </Card>

        {/* Results Count */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-succession-text-secondary">
            {filteredStudents.length} {filteredStudents.length === 1 ? 'student' : 'students'} found
          </p>
        </div>

        {/* Students Grid */}
        {filteredStudents.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredStudents.map((student) => (
              <StudentCard
                key={student.id}
                student={student}
                onClick={setSelectedStudent}
                isMessaging={messagingWithStudentIds && messagingWithStudentIds.has(student.user?.id)}
              />
            ))}
          </div>
        ) : (
          <Card className="text-center py-12">
            <AcademicCapIcon className="mx-auto h-12 w-12 text-succession-text-muted mb-4" />
            <h3 className="text-lg font-semibold text-succession-text-primary mb-2">
              {hasActiveFilters ? 'No students match your filters' : 'No student prospects yet'}
            </h3>
            <p className="text-succession-text-secondary mb-2">
              {hasActiveFilters
                ? 'Try adjusting your search criteria'
                : 'Student profiles will appear here once students sign up and create their profiles.'}
            </p>
            {!hasActiveFilters && (
              <p className="text-xs text-succession-text-muted mt-4">
                Students can sign up at <span className="font-mono">/student</span> to create their profiles
              </p>
            )}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="mt-4 px-4 py-2 bg-succession-primary text-white rounded-md hover:bg-succession-primary-dark"
              >
                Clear Filters
              </button>
            )}
          </Card>
        )}

        {/* Student Profile Modal */}
        {selectedStudent && (
          <StudentProfileModal
            student={selectedStudent}
            currentUser={currentUser}
            onClose={() => setSelectedStudent(null)}
            isMessaging={messagingWithStudentIds && messagingWithStudentIds.has(selectedStudent.user?.id)}
          />
        )}
    </SingleColumnTemplate>
  );
};

StudentProspectsPage.propTypes = {
  currentUser: PropTypes.object,
  students: PropTypes.array.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  filters: PropTypes.object,
  messagingWithStudentIds: PropTypes.instanceOf(Set),
};

export default StudentProspectsPage;

